'use client';
import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface CaseStudy {
  brand: string;
  category: string;
  reviews: number;
  avgRating: number;
  slotsFilled: number;
  totalSlots: number;
  improvement: string;
}

export default function CaseStudiesGrid() {
  const [caseStudies, setCaseStudies] = useState<CaseStudy[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchCaseStudies() {
      const supabase = createClient();
      try {
        const { data, error } = await supabase
          .from('campaigns')
          .select('brand, category, filled_slots, total_slots, avg_rating')
          .eq('campaign_status', 'completed')
          .order('filled_slots', { ascending: false })
          .limit(6);

        if (error || !data || data.length === 0) {
          setCaseStudies([]);
        } else {
          setCaseStudies(
            data.map((c) => ({
              brand: c.brand || 'Brand',
              category: c.category || 'General',
              reviews: c.filled_slots || 0,
              avgRating: c.avg_rating || 0,
              slotsFilled: c.filled_slots || 0,
              totalSlots: c.total_slots || 0,
              improvement: `+${Math.round(((c.filled_slots || 0) / Math.max(c.total_slots || 1, 1)) * 100)}% slots filled`,
            }))
          );
        }
      } catch (err) {
        console.log('Case studies fetch error:', err);
        setCaseStudies([]);
      } finally {
        setLoading(false);
      }
    }
    fetchCaseStudies();
  }, []);

  return (
    <section className="section-py bg-surface-variant">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary bg-primary-container px-3 py-1 rounded-full mb-3">
            Case Studies
          </span>
          <h2 className="font-heading font-bold text-foreground" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
            Brands That Grew With Us
          </h2>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-card rounded-2xl border p-6 animate-pulse space-y-4">
                <div className="h-5 bg-muted rounded w-1/2" />
                <div className="grid grid-cols-2 gap-3">
                  <div className="h-16 bg-muted rounded-xl" />
                  <div className="h-16 bg-muted rounded-xl" />
                </div>
              </div>
            ))}
          </div>
        ) : caseStudies.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            Case studies coming soon as campaigns complete.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {caseStudies.map((cs, idx) => (
              <div
                key={`${cs.brand}-${idx}`}
                className="bg-card rounded-2xl border-l-4 border-t border-r border-b p-6 space-y-4"
                style={{ borderLeftColor: '#FF6B35' }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-heading font-bold text-foreground">{cs.brand}</div>
                    <div className="text-xs text-muted-foreground">{cs.category}</div>
                  </div>
                  <div className="w-10 h-10 secondary-gradient rounded-xl flex items-center justify-center">
                    <Icon name="BuildingStorefrontIcon" size={18} className="text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-surface-variant rounded-xl p-3 text-center">
                    <div className="font-heading font-bold text-lg text-primary">{cs.reviews}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Reviews</div>
                  </div>
                  <div className="bg-surface-variant rounded-xl p-3 text-center">
                    <div className="font-heading font-bold text-lg text-foreground">
                      {(cs.avgRating || 0).toFixed(1)}★
                    </div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Avg Rating</div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{cs.slotsFilled}/{cs.totalSlots} slots filled</span>
                </div>
                <div className="flex items-center gap-2 bg-green-50 rounded-xl px-3 py-2">
                  <Icon name="ArrowTrendingUpIcon" size={14} className="text-accent" />
                  <span className="text-xs font-semibold text-accent">{cs.improvement}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}